import React, { useCallback, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable as useSortableItem,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import { EditorField, FormFieldType } from "./types";

interface SortableFieldItemProps {
    field: EditorField;
    onUpdate: (id: string, updates: Partial<EditorField>) => void;
    onDelete: (id: string) => void;
    onDuplicate: (id: string) => void;
    validationError?: string;
}

const fieldTypeTranslationKey: Record<FormFieldType, string> = {
    approveReject: "settings.formEditor.approveReject",
    freeText: "settings.formEditor.freeText",
    multiLineText: "settings.formEditor.multiLineText",
    number: "settings.formEditor.number",
    checkbox: "settings.formEditor.checkbox",
    dropdown: "settings.formEditor.dropdown",
    attachment: "settings.formEditor.attachment",
};

const fieldTypeColor: Record<
    FormFieldType,
    "primary" | "secondary" | "success" | "warning" | "info" | "error"
> = {
    approveReject: "primary",
    freeText: "secondary",
    multiLineText: "secondary",
    number: "info",
    checkbox: "success",
    dropdown: "warning",
    attachment: "error",
};

interface SortableOptionItemProps {
    optionId: string;
    index: number;
    value: string;
    onChange: (index: number, value: string) => void;
    onRemove: (index: number) => void;
    canRemove: boolean;
    placeholder: string;
    fieldId: string;
}

const SortableOptionItem = ({
    optionId,
    index,
    value,
    onChange,
    onRemove,
    canRemove,
    placeholder,
    fieldId,
}: SortableOptionItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortableItem({ id: optionId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    cursor: "grab",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <DragIndicatorIcon
                    fontSize="small"
                    color="action"
                    sx={{ fontSize: 16 }}
                />
            </Box>
            <TextField
                size="small"
                value={value}
                onChange={(e) => onChange(index, e.target.value)}
                placeholder={placeholder}
                sx={{ flex: 1 }}
                data-testid={`form-field-option-${fieldId}-${index}`}
            />
            <IconButton
                size="small"
                color="error"
                onClick={() => onRemove(index)}
                disabled={!canRemove}
            >
                <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export const SortableFieldItem = ({
    field,
    onUpdate,
    onDelete,
    onDuplicate,
    validationError,
}: SortableFieldItemProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const optionSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
        })
    );

    const optionIds = (field.options ?? []).map((_, i) => `option_${i}`);

    const handleAddOption = useCallback(() => {
        const options = [...(field.options ?? []), ""];
        onUpdate(field.id, { options });
    }, [field.id, field.options, onUpdate]);

    const handleRemoveOption = useCallback(
        (index: number) => {
            const options = (field.options ?? []).filter((_, i) => i !== index);
            onUpdate(field.id, { options });
        },
        [field.id, field.options, onUpdate]
    );

    const handleOptionChange = useCallback(
        (index: number, value: string) => {
            const options = [...(field.options ?? [])];
            options[index] = value;
            onUpdate(field.id, { options });
        },
        [field.id, field.options, onUpdate]
    );

    const handleOptionDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                const oldIndex = optionIds.indexOf(active.id as string);
                const newIndex = optionIds.indexOf(over.id as string);
                const newOptions = arrayMove(
                    field.options ?? [],
                    oldIndex,
                    newIndex
                );
                onUpdate(field.id, { options: newOptions });
            }
        },
        [field.id, field.options, onUpdate, optionIds]
    );

    const hasError = !!validationError;
    const optionCount = (field.options ?? []).length;

    const validationErrorText = validationError
        ? t(`settings.formEditor.validation.${validationError}`)
        : undefined;

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            elevation={isDragging ? 4 : 1}
            sx={{
                p: 2,
                mb: 1,
                borderLeft: hasError ? 3 : 0,
                borderColor: hasError ? "error.main" : undefined,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: "grab",
                        display: "flex",
                        alignItems: "center",
                    }}
                    title={t("settings.formEditor.dragToReorder")}
                >
                    <DragIndicatorIcon color="action" />
                </Box>
                <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                    {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                    ) : (
                        <ExpandMoreIcon fontSize="small" />
                    )}
                </IconButton>
                <Chip
                    label={t(fieldTypeTranslationKey[field.type])}
                    color={fieldTypeColor[field.type]}
                    size="small"
                    variant="outlined"
                />
                <TextField
                    size="small"
                    label={t("settings.formEditor.fieldLabel")}
                    value={field.label}
                    onChange={(e) =>
                        onUpdate(field.id, { label: e.target.value })
                    }
                    sx={{ flex: 1 }}
                    error={validationError === "emptyLabel"}
                    data-testid={`form-field-label-${field.id}`}
                />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={field.required}
                            onChange={(e) =>
                                onUpdate(field.id, {
                                    required: e.target.checked,
                                })
                            }
                            data-testid={`form-field-required-${field.id}`}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            {t("settings.formEditor.fieldRequired")}
                        </Typography>
                    }
                />
                <IconButton
                    size="small"
                    onClick={() => onDuplicate(field.id)}
                    title={t("settings.formEditor.duplicateField")}
                    data-testid={`form-field-duplicate-${field.id}`}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(field.id)}
                    title={t("settings.formEditor.deleteField")}
                    data-testid={`form-field-delete-${field.id}`}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>
            {hasError && validationErrorText && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ ml: 9, display: "block" }}
                >
                    {validationErrorText}
                </Typography>
            )}
            <Collapse in={expanded}>
                <Box sx={{ mt: 1, ml: 5 }}>
                    <TextField
                        size="small"
                        label={t("settings.formEditor.fieldDescription")}
                        value={field.description ?? ""}
                        onChange={(e) =>
                            onUpdate(field.id, {
                                description: e.target.value || undefined,
                            })
                        }
                        fullWidth
                    />
                </Box>
                {field.type === "number" && (
                    <Box
                        sx={{
                            mt: 1,
                            ml: 5,
                            display: "flex",
                            gap: 2,
                        }}
                    >
                        <TextField
                            size="small"
                            type="number"
                            label={t("settings.formEditor.minValue")}
                            value={field.min ?? ""}
                            onChange={(e) =>
                                onUpdate(field.id, {
                                    min:
                                        e.target.value === ""
                                            ? undefined
                                            : Number(e.target.value),
                                })
                            }
                            sx={{ width: 120 }}
                            error={validationError === "minGreaterThanMax"}
                        />
                        <TextField
                            size="small"
                            type="number"
                            label={t("settings.formEditor.maxValue")}
                            value={field.max ?? ""}
                            onChange={(e) =>
                                onUpdate(field.id, {
                                    max:
                                        e.target.value === ""
                                            ? undefined
                                            : Number(e.target.value),
                                })
                            }
                            sx={{ width: 120 }}
                            error={validationError === "minGreaterThanMax"}
                        />
                    </Box>
                )}
                {(field.type === "freeText" ||
                    field.type === "multiLineText") && (
                    <Box sx={{ mt: 1, ml: 5 }}>
                        <TextField
                            size="small"
                            type="number"
                            label={t("settings.formEditor.maxLength")}
                            value={field.maxLength ?? ""}
                            onChange={(e) =>
                                onUpdate(field.id, {
                                    maxLength:
                                        e.target.value === ""
                                            ? undefined
                                            : Number(e.target.value),
                                })
                            }
                            sx={{ width: 150 }}
                            inputProps={{ min: 1 }}
                        />
                    </Box>
                )}
                {(field.type === "dropdown" ||
                    field.type === "approveReject") && (
                    <Box sx={{ mt: 1, ml: 5 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            {t("settings.formEditor.dropdownOptions")}
                        </Typography>
                        <DndContext
                            sensors={optionSensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleOptionDragEnd}
                        >
                            <SortableContext
                                items={optionIds}
                                strategy={verticalListSortingStrategy}
                            >
                                {(field.options ?? []).map((option, index) => (
                                    <SortableOptionItem
                                        key={optionIds[index]}
                                        optionId={optionIds[index]}
                                        index={index}
                                        value={option}
                                        onChange={handleOptionChange}
                                        onRemove={handleRemoveOption}
                                        canRemove={optionCount > 2}
                                        placeholder={t(
                                            "settings.formEditor.optionLabel"
                                        )}
                                        fieldId={field.id}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleAddOption}
                            title={t("settings.formEditor.addOption")}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
                {field.type === "attachment" && (
                    <Box sx={{ mt: 1, ml: 5 }}>
                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            <TextField
                                size="small"
                                type="number"
                                label={t("settings.formEditor.maxFiles")}
                                value={field.maxFiles ?? ""}
                                onChange={(e) =>
                                    onUpdate(field.id, {
                                        maxFiles:
                                            e.target.value === ""
                                                ? undefined
                                                : Number(e.target.value),
                                    })
                                }
                                sx={{ width: 180 }}
                                inputProps={{ min: 1, max: 20 }}
                            />
                            <TextField
                                size="small"
                                type="number"
                                label={t("settings.formEditor.maxFileSize")}
                                value={field.maxFileSize ?? ""}
                                onChange={(e) =>
                                    onUpdate(field.id, {
                                        maxFileSize:
                                            e.target.value === ""
                                                ? undefined
                                                : Number(e.target.value),
                                    })
                                }
                                sx={{ width: 220 }}
                                inputProps={{ min: 1, max: 50 }}
                            />
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            {t("settings.formEditor.acceptedFileTypes")}
                        </Typography>
                        {[
                            {
                                label: t("settings.formEditor.fileTypeImages"),
                                types: ["image/png", "image/jpeg"],
                            },
                            {
                                label: t("settings.formEditor.fileTypePdf"),
                                types: ["application/pdf"],
                            },
                        ].map((group) => {
                            const current = field.acceptedFileTypes ?? [];
                            const allIncluded = group.types.every((t) =>
                                current.includes(t)
                            );
                            return (
                                <FormControlLabel
                                    key={group.types[0]}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={allIncluded}
                                            onChange={(e) => {
                                                const next = e.target.checked
                                                    ? [
                                                        ...new Set([
                                                            ...current,
                                                            ...group.types,
                                                        ]),
                                                    ]
                                                    : current.filter(
                                                        (t) =>
                                                            !group.types.includes(
                                                                t
                                                            )
                                                    );
                                                onUpdate(field.id, {
                                                    acceptedFileTypes: next,
                                                });
                                            }}
                                        />
                                    }
                                    label={group.label}
                                />
                            );
                        })}
                    </Box>
                )}
            </Collapse>
        </Paper>
    );
};
